import { useCallback } from "react";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { MESSAGES } from "~/resources/Message";
import { FormScript, FormScriptResponse } from "~/models/Commons ";
import { useSaveScriptMutation } from "~/services/script";
import { useGetCloudEnvironmentsQuery } from "~/services/cloudenvironment";

export const UseFormScript = (
  editedItem: FormScriptResponse | null,
  onCloseModal: () => void,
  refetch: () => void
) => {
  const [saveScript, { isLoading }] = useSaveScriptMutation();
  const { data: cloudEnvData } = useGetCloudEnvironmentsQuery({
    page: 0,
    size: 100,
    active: true,
  });

  const {
    formState: { errors },
    handleSubmit,
    control,
    setValue,
    setError,
    clearErrors,
    watch,
  } = useForm<FormScript>({
    defaultValues: {
      title: editedItem?.title ?? "",
      description: editedItem?.description ?? "",
      location: editedItem?.location ?? "",
      cloudEnvironmentId: undefined,
      script: null,
      configFiles: null,
    },
  });

  const currentScript = watch("script");
  const currentConfigFiles = watch("configFiles");

  // Handler for script file drop
  const onScriptDrop = useCallback(
    (acceptedFiles: File[]) => {
      const file = acceptedFiles?.[0] || null;

      setValue("script", file);
      if (file) {
        clearErrors("script");
      } else {
        setError("script", {
          type: "manual",
          message: MESSAGES.MessageUploadScript,
        });
      }
    },
    [setValue, clearErrors, setError]
  );

  // Handler for config files drop (not required, so no error if null)
  const onConfigDrop = useCallback(
    (acceptedFiles: File[]) => {
      const file = acceptedFiles?.[0] || null;

      setValue("configFiles", file);
      if (file) {
        clearErrors("configFiles");
      }
      // No error set if file is null, as configFiles is optional
    },
    [setValue, clearErrors]
  );

  const submitForm = async (_data: FormScript) => {
    const data = { ..._data };
    try {
      // Prepare API payload separately to avoid mutating typed form data
      const payload: FormScript = {
        ...data,
        cloudEnvironmentId: data.cloudEnvironmentId,
      };
      const response = await saveScript(payload).unwrap();
      if (response.success) {
        toast.success(response?.message);
        refetch();
      } else {
        throw new Error("Save operation failed: No success response");
      }
    } catch (error: any) {
      const errorMessageTitle =
        error?.data?.title || error?.title || MESSAGES.MessageErrorSavingScript;
      const errorMessageStstus =
        error?.data?.status ||
        error?.status ||
        MESSAGES.MessageErrorSavingScript;
      toast.error(`Status:${errorMessageStstus}\nTitle:${errorMessageTitle}`);
      console.error("Error", error);
    } finally {
      refetch();
      onCloseModal();
      console.log("submitForm: completed");
    }
  };

  return {
    handleSubmit,
    control,
    errors,
    currentScript,
    currentConfigFiles,
    onScriptDrop,
    onConfigDrop,
    submitForm,
    isLoading,
    cloudEnvData,
  };
};
